import { AfterSaleStatus, AfterSaleType, FullfillmentType, TransactionReferenceRelatedIdType, TransactionType, VerifyOrderDataIntegrity_OrderFragment } from "../codegen/graphql";
import gql from "./dummyGql";


export const VerifyOrderDataIntegrity_Order = gql`
  fragment VerifyOrderDataIntegrity_Order on Order {
    id
    orderProductsVariations {
      id
      quantity
      adVariation {
        ad {
          title
        }
      }
      supplierProductVariation {
        sku
        supplierProductId
        supplierName
      }
      restockShipmentProductVariation {
        supplierProductVariationSku
        supplierProductVariationSupplierProductId
      }
    }

    orderFulfillments {
      id
      type
      fullfilledStatus
      linkedLineItems {
        orderFullfillmentId
        orderItemId
        quantity
      }
    }

    transactions {
      lineItemId
      type
      references {
        transactionId
        relatedId
        relatedIdType
      }
    }
    afterSales {
      id
      lineItemId
      type
      status
      returnPackageId
      lineItemQuantity
      restockedQuantity
    }
  }
`

export function verifyOrderDataIntegrity(
  order: VerifyOrderDataIntegrity_OrderFragment
) {
  const results: {
    title: string;
    description: string;
    result: boolean;
    severity: 'error' | 'warning';
    debug: any;
  }[] = [];

  const transactionsByLineItemId = order.transactions.reduce((acc, transaction) => {
    if (!acc[transaction.lineItemId]) {
      acc[transaction.lineItemId] = [];
    }
    acc[transaction.lineItemId].push(transaction);
    return acc;
  }, {} as Record<string, VerifyOrderDataIntegrity_OrderFragment['transactions']>);

  const fulfillmentsByLineItemId = order.orderFulfillments.reduce((acc, fulfillment) => {
    for (const lineItem of fulfillment.linkedLineItems) {
      if (!acc[lineItem.orderItemId]) {
        acc[lineItem.orderItemId] = [];
      }
      acc[lineItem.orderItemId].push(fulfillment);
    }
    return acc;
  }, {} as Record<string, VerifyOrderDataIntegrity_OrderFragment['orderFulfillments']>);

  const transactionsByReference = order.transactions.reduce((acc, transaction) => {
    for (const reference of transaction.references) {
      const referenceKey = `${reference.relatedIdType}_${reference.relatedId}`;
      if (!acc[referenceKey]) {
        acc[referenceKey] = [];
      }
      acc[referenceKey].push(transaction);
    }
    return acc;
  }, {} as Record<string, VerifyOrderDataIntegrity_OrderFragment['transactions']>);

  const afterSalesByLineItemId = order.afterSales.reduce((acc, afterSale) => {
    if (!acc[afterSale.lineItemId]) {
      acc[afterSale.lineItemId] = [];
    }
    acc[afterSale.lineItemId].push(afterSale);
    return acc;
  }, {} as Record<string, VerifyOrderDataIntegrity_OrderFragment['afterSales']>);

  /*
   * Line item checks
   */
  for (const orderProductsVariation of order.orderProductsVariations) {
    const lineItemDebugData = {
      ['Line item ID']: orderProductsVariation.id,
      ['Line item title']: orderProductsVariation.adVariation?.ad?.title,
    };

    const hasFullyCancelledAfterSale = afterSalesByLineItemId[orderProductsVariation.id]?.some(afterSale => (
      afterSale.type === AfterSaleType.Cancellation
      && afterSale.status === AfterSaleStatus.Approved
      && afterSale.restockedQuantity === orderProductsVariation.quantity
    ));

    /*
     * It should have a supplier product variation,
     * Unless the line item has a matching cancelled after-sale
     */
    const hasSupplierProductVariation = !!orderProductsVariation.supplierProductVariation;
    results.push({
      title: 'Line item must have a supplier product variation, unless it has been cancelled',
      description: 'Every line item must have a supplier product variation, which is the supplier that is used to fulfill this order.',
      result: (
        hasSupplierProductVariation
        || (!hasSupplierProductVariation && hasFullyCancelledAfterSale)
      ),
      severity: 'error',
      debug: lineItemDebugData,
    });

    /*
     * Detailed checks on supplier product variation, for own stock
     */
    if (
      orderProductsVariation.supplierProductVariation
      // Aliexpress product do not have restock shipment product variations
      && orderProductsVariation.supplierProductVariation.supplierName !== 'aliexpress'
    ) {
      /*
       * It should have a restock shipment product variation
       */
      const restockShipmentProductVariation = orderProductsVariation.restockShipmentProductVariation;
      const hasRestockShipmentProductVariation = !!restockShipmentProductVariation;
      results.push({
        title: 'Restock shipment product variation must exist',
        description: 'For local stock line items, a restock shipment product variation must exist, which is the restocked shipment that is used to fulfill this order.',
        result: hasRestockShipmentProductVariation,
        severity: 'error',
        debug: lineItemDebugData,
      });

      if (restockShipmentProductVariation) {
        /*
         * The restock and the line item should have the same supplier product variation
         */
        const isMatchingSupplierProductVariation = (
          restockShipmentProductVariation
          && restockShipmentProductVariation.supplierProductVariationSku === orderProductsVariation.supplierProductVariation.sku
          && restockShipmentProductVariation.supplierProductVariationSupplierProductId === orderProductsVariation.supplierProductVariation.supplierProductId
        )
        results.push({
          title: 'Restock shipment product variation does not match order product variation',
          description: 'The restock shipment product variation must match the order product variation, meaning it is the same kind of product variation.',
          result: isMatchingSupplierProductVariation,
          severity: 'error',
          debug: lineItemDebugData,
        });
      }
    }

    /*
     * Check on line item related transactions
     */
    const countPerTransactionType = order.transactions.reduce((acc, transaction) => {
      if (transaction.lineItemId !== Number(orderProductsVariation.id)) return acc;
      acc[transaction.type] = (acc[transaction.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    /*
     * It should have 1 product cost transaction
     */
    const hasOnlyOneProductCost = countPerTransactionType[TransactionType.ProductCost] === 1;
    results.push({
      title: 'Line item must have a product cost transaction',
      description: 'Every line item must have a product cost transaction, and only one.',
      result: (
        hasOnlyOneProductCost
        || hasFullyCancelledAfterSale // not necessary if it has been fully cancelled
      ),
      severity: 'error',
      debug: lineItemDebugData,
    });

    /*
     * It should have 1 gross sale transaction
     */
    const hasOnlyOneGrossSale = countPerTransactionType[TransactionType.GrossSale] === 1;
    results.push({
      title: 'Line item must have a gross sale transaction',
      description: 'Every line item must have a gross sale transaction, and only one.',
      result: hasOnlyOneGrossSale,
      severity: 'error',
      debug: lineItemDebugData,
    });

    /*
     * It should have at least one channel fee transaction (multiple fees can occur with Ebay for example)
     */
    const hasAtLeastOneChannelFee = countPerTransactionType[TransactionType.ChannelFee] >= 1;
    results.push({
      title: 'Line item must have at least one channel fee transaction',
      description: 'Every line item must have at least one channel fee transaction.',
      result: hasAtLeastOneChannelFee,
      severity: 'error',
      debug: lineItemDebugData,
    });


    /*
    * Checking order fulfillments
    */
    const lineItemFulfillments = fulfillmentsByLineItemId[orderProductsVariation.id];
    const mustHaveOnlyOneOutwardFulfillment = lineItemFulfillments?.filter(fulfillment => (
      fulfillment.type === FullfillmentType.Outward
    )).length === 1;
    results.push({
      title: 'Line item must have only one outward fulfillment, except if it has been cancelled',
      description: 'Every line item must have only one outward fulfillment, except if it has been cancelled.',
      result: (
        mustHaveOnlyOneOutwardFulfillment
        || hasFullyCancelledAfterSale // not necessary if it has been fully cancelled
      ),
      severity: 'error',
      debug: lineItemDebugData,
    });
    
    if (mustHaveOnlyOneOutwardFulfillment && !hasFullyCancelledAfterSale) {
      const hasOneShippingCostTransaction = transactionsByLineItemId[orderProductsVariation.id]?.filter(transaction => (
        transaction.type === TransactionType.ShippingCost
      )).length === 1;
      results.push({
        title: 'Line item must have a shipping cost transaction',
        description: 'Every line item must have a shipping cost transaction, and only one.',
        result: hasOneShippingCostTransaction,
        severity: 'error',
        debug: lineItemDebugData,
      });
    }

    /*
     * Checking after-sales
     */
    const lineItemAfterSales = afterSalesByLineItemId[orderProductsVariation.id];
    if (lineItemAfterSales) {
      for (const afterSale of lineItemAfterSales) {
        if (afterSale.status !== AfterSaleStatus.Approved) {
          continue;
        }

        const afterSaleKey = `${TransactionReferenceRelatedIdType.AfterSale}_${afterSale.id}`;
        const afterSalesTransactions = transactionsByReference[afterSaleKey];
        /*
         * The after-sale should have 1 refund gross sale transaction
         */
        const hasOnlyOneRefundGrossSale = afterSalesTransactions
          ? afterSalesTransactions.filter(transaction => (
            transaction.type === TransactionType.RefundGrossSale
          )).length === 1
          : false;
        results.push({
          title: 'After-sale should have a refund gross sale transaction',
          description: 'Every after-sale should have a refund gross sale transaction.',
          result: hasOnlyOneRefundGrossSale,
          severity: 'error',
          debug: lineItemDebugData,
        });

        /*
         * The after-sale should have at least 1 refund channel fee transaction
         */
        const hasAtLeastOneRefundChannelFee = afterSalesTransactions
          ? afterSalesTransactions.filter(transaction => (
            transaction.type === TransactionType.RefundChannelFee
          )).length >= 1
          : false;
        results.push({
          title: 'After-sale should have at least one refund channel fee transaction',
          description: 'Every after-sale should have at least one refund channel fee transaction.',
          result: hasAtLeastOneRefundChannelFee,
          severity: 'error',
          debug: lineItemDebugData,
        });

        /*
         * Only if the product has been restocked, it should have a refund product cost transaction
         * If there is no product_cost transaction, which can happen if the item has not been fulfilled,
         * because of an early cancellation for example,
         * it's not necessary
         */
        if (
          afterSale.restockedQuantity
          && afterSale.restockedQuantity > 0
          && transactionsByLineItemId[orderProductsVariation.id]?.some(transaction => (
            transaction.type === TransactionType.ProductCost
          ))
        ) {
          const hasOnlyOneRefundProductCost = afterSalesTransactions
            ? afterSalesTransactions.filter(transaction => (
              transaction.type === TransactionType.RefundProductCost
            )).length === 1
            : false;
          results.push({
            title: 'After-sale should have a refund product cost transaction, only if the product has been restocked',
            description: 'Every after-sale should have a refund product cost transaction, only if the product has been restocked.',
            result: hasOnlyOneRefundProductCost,
            severity: 'error',
            debug: lineItemDebugData,
          });
        }

        /*
         * If it is a return after-sale, it should have a matching return fulfillment
         */
        if (afterSale.type === AfterSaleType.Return) {
          const lineItem = order.orderProductsVariations.find(variation => variation.id === afterSale.lineItemId)!;
          
          // Aliexpress returns
          if (lineItem.supplierProductVariation?.supplierName === 'aliexpress') {
            const fulfillments = fulfillmentsByLineItemId[orderProductsVariation.id];

            if (!fulfillments || fulfillments.length !== 1 || fulfillments[0].type !== FullfillmentType.Outward) {
              results.push({
                title: 'Return after-sale for aliexpress should have only one outward fulfillment, and not return fulfillment',
                description: 'Every return after-sale for aliexpress should have only one outward fulfillment, and not return fulfillment.',
                result: true,
                severity: 'error',
                debug: lineItemDebugData,
              });
            }
            else {
              // Approved after sales from aliexpress should be marked as refunded when after sale is completed
              if (afterSale.status === AfterSaleStatus.Approved) {
                results.push({
                  title: 'Return after-sale should be marked as refunded when after sale is completed',
                  description: 'Returned after sales from aliexpress should be marked as refunded when after sale is completed.',
                  result: true,
                  severity: 'error',
                  debug: lineItemDebugData,
                });
              }
            }
          }
          // Our own stock returns
          else {
            // Returned after sales from our own stock should have a matching return fulfillment
            const hasMatchingReturnFulfillment = order.orderFulfillments.some(fulfillment => (
              fulfillment.id === afterSale.returnPackageId
              && fulfillment.type === FullfillmentType.Return
              && fulfillment.linkedLineItems.some(lineItem => (
                lineItem.orderItemId === afterSale.lineItemId
                && lineItem.quantity === afterSale.lineItemQuantity
              ))
            ));
            results.push({
              title: 'Return after-sale should have a matching return fulfillment',
              description: 'Every return after-sale should have a matching return fulfillment.',
              result: hasMatchingReturnFulfillment,
              severity: 'error',
              debug: lineItemDebugData,
            });
          }
        }
      }
    }

  }

  return results;
}